import React, { FC, useCallback, useEffect, useState, KeyboardEvent } from 'react';
import debounce from 'lodash/debounce';
import { Search } from 'lucide-react';
import { animateScroll as scroll } from 'react-scroll';
import { connect, ConnectedProps } from 'react-redux';
import { useRouter } from 'next/router';

import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '@/lib/utils';
import * as actions from '../../actions';
import { ROUTE_PATH, SEARCH_ALL_LIMIT } from '../../constants';
import { RootState } from '../../types/redux';

const DEBOUNCE_TIME = 800;
const DEFAULT_PLATFORM = 'ptt';

type Platform = 'ptt' | 'dcard';

const mapStateToProps = ({ search }: RootState) => ({
  isFetching: search.isFetching || false,
});

const mapDispatchToProps = {
  SearchAll: actions.SearchAll,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const SearchBar: FC<PropsFromRedux> = (props) => {
  const router = useRouter();
  const { isFetching } = props;
  const [platform, setPlatform] = useState<Platform>(DEFAULT_PLATFORM);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const searchAction = useCallback(
    debounce((_platform: Platform, _searchTerm: string) => {
      props.SearchAll(_platform, _searchTerm, SEARCH_ALL_LIMIT, 0);
      router.push({
        pathname: ROUTE_PATH.SEARCH,
        query: {
          platform: _platform,
          q: _searchTerm,
        },
      });
    }, DEBOUNCE_TIME),
    [props, router],
  );

  useEffect(() => {
    if (searchTerm) {
      searchAction(platform, searchTerm);
    }
  }, [platform, searchTerm, searchAction]);

  const onKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchTerm) {
      searchAction.flush();
    }
  };

  const handleDropdownChange = (value: string) => {
    setPlatform(value as Platform);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="relative flex items-center w-full">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          className="pl-10 pr-2"
          placeholder="搜尋 [板, 文章, 使用者]"
          onChange={handleInputChange}
          onKeyPress={onKeyPress}
          value={searchTerm}
          disabled={isFetching}
        />
        {isFetching && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
          </div>
        )}
      </div>
      <Select value={platform} onValueChange={handleDropdownChange}>
        <SelectTrigger className="w-24 ml-2">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ptt">PTT</SelectItem>
          <SelectItem value="dcard">Dcard</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};

// @ts-ignore - TypeScript issue with React 18 and react-redux types
export default connector(SearchBar) as React.ComponentType<{}>;